import { useState } from 'react'

import { convertGrapeMoneyToNumber } from 'utils'
import {
  countEmployeesWithCoverageCorrections,
  countEmployeesWithPrefunding,
  countEmployeesWithQME,
  formatMonthDateRange,
  formatMonthName,
  formatNextMonthDateRange,
  getNextMonthName,
  getPriorMonthEnrollmentCutoffDate,
} from 'utils/billing'

import { CostBreakdownTable } from './CostBreakdownTable'
import { useInvoice } from './InvoiceContext'
import { InvoiceCostBreakdownMobile } from './mobile'
import { WhyBilledMoreThanOneMonthModal } from './modals/WhyBilledMoreThanOneMonthModal'
import { type CostBreakdownItem } from './types'

export function InvoiceCostBreakdown () {
  const { invoice, allMemberInvoices, amounts, isAmountsLoading } = useInvoice()
  const memberInvoices = allMemberInvoices || []
  const [isModalOpen, setIsModalOpen] = useState(false)

  const items: CostBreakdownItem[] = [
    {
      title: `Pre-payment for ${getNextMonthName(invoice.period_end_date)} insurance coverage`,
      description: `Billing in advance for coverage from ${formatNextMonthDateRange(invoice.period_end_date)}.`,
      employeeCount: countEmployeesWithPrefunding(memberInvoices),
      amount: amounts?.insurance_premiums_total?.formatted_amount || '$0.00',
    },
    ...(amounts?.has_double_billing &&
    amounts?.double_billing_total &&
    convertGrapeMoneyToNumber(amounts.double_billing_total) > 0
      ? [
          {
            title: `Payment for ${formatMonthName(invoice.period_end_date)} Insurance coverage`,
            description: `Insurance coverage for new employees who enrolled for ${formatMonthDateRange(invoice.period_end_date)} coverage after ${getPriorMonthEnrollmentCutoffDate(invoice.period_end_date)}.`,
            employeeCount: amounts?.double_billing_employee_count || 0,
            amount: amounts.double_billing_total.formatted_amount || '$0.00',
            modalTrigger: (
              <button
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='body-sm text-purple-600 underline decoration-dotted'
              >
                Why was I billed for more than 1 month of premiums?
              </button>
            ),
          },
        ]
      : []),
    {
      title: `Changes in ${formatMonthName(invoice.period_end_date)} Insurance Coverage`,
      description: 'Includes factors like new dependents, moving, or cancelling plans that cause costs to change.',
      employeeCount: countEmployeesWithCoverageCorrections(memberInvoices),
      amount: amounts?.coverage_corrections_excluding_double_billing?.formatted_amount ||
        amounts?.coverage_corrections?.formatted_amount ||
        '$0.00',
    },
    {
      title: 'Qualified medical expenses',
      description: 'Money your team used from their leftover allowance to cover copays, prescriptions, etc.',
      employeeCount: countEmployeesWithQME(memberInvoices),
      amount: amounts?.other?.formatted_amount || '$0.00',
    },
  ]

  return (
    <>
      <CostBreakdownTable items={items} isLoading={isAmountsLoading} />
      <InvoiceCostBreakdownMobile items={items} isLoading={isAmountsLoading} />
      <WhyBilledMoreThanOneMonthModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  )
}
